<template>
  <Modal
    :is-visible="errorVisible.neighborState"
    no-cancel
    :tracking="{ action: 'checkout', topicKey: 'neighbor-state-restriction' }"
    @confirm="handleConfirm('neighborState')"
  >
    <template #headline>
      {{ $t('checkout.restrictions.geocheck.neighborstate.headline') }}
    </template>
    <i18n-t keypath="checkout.restrictions.geocheck.neighborstate.message" scope="global" tag="p">
      <template #contactSupport>
        <a class="text-link" :href="$localePath('/service?chat=true')">
          {{ $t('common.sentences.contactsupport').toLowerCase() }}
        </a>
      </template>
    </i18n-t>
    <template #confirm>
      {{ $t('checkout.restrictions.geocheck.neighborstate.confirm') }}
    </template>
  </Modal>
  <ComplianceErrorsModal
    v-if="errorVisible.geoCheck"
    :error="complianceError"
    tracking-action="checkout"
    @confirm="handleConfirm('geoCheck')"
  />
  <CheckoutLimitModal
    v-if="errorVisible.depositLimit"
    v-bind="{
      balance,
      grossTotal,
    }"
    :available-amount="restrictions.depositLimit!.available"
    :is-visible="errorVisible.depositLimit"
    :limit="restrictions.depositLimit!.value"
    :period="restrictions.depositLimit!.period"
    type="deposit"
    @confirm="handleConfirm('depositLimit')"
  />
  <CheckoutLimitModal
    v-if="errorVisible.spendingLimit"
    v-bind="{
      grossTotal,
    }"
    :available-amount="restrictions.spendingLimit!.available"
    :is-visible="errorVisible.spendingLimit"
    :limit="restrictions.spendingLimit!.value"
    :period="restrictions.spendingLimit!.period"
    type="spending"
    @confirm="handleConfirm('spendingLimit')"
  />
  <CheckoutRestrictionsPlayerModal
    v-if="errorVisible.playerRestrictions"
    :status="restrictions.playerCheck"
  />
</template>

<script lang="ts" setup>
import {
  GeoCheckStatus,
  type Money,
  PlayerCheckStatus,
  type Restrictions,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { ComplianceError } from '~/composables/compliance'

type CheckoutRestrictionsProps = {
  balance: Money
  closeAllModalsOnConfirm?: boolean
  grossTotal?: Money
  restrictions: Restrictions
}

const { closeAllModalsOnConfirm, restrictions } = defineProps<CheckoutRestrictionsProps>()

const errorVisible = ref({
  depositLimit: false,
  geoCheck: false,
  neighborState: false,
  playerRestrictions: false,
  spendingLimit: false,
})

const complianceError = ref<ComplianceError>(ComplianceError.GeoCheckGeneral)

const checkRestrictions = () => {
  if (!restrictions) return

  if (restrictions.geoCheck !== GeoCheckStatus.Ok) {
    switch (restrictions.geoCheck) {
      case GeoCheckStatus.IpHostingFacilityDetected:
        errorVisible.value.geoCheck = true
        complianceError.value = ComplianceError.IpHostingFacilityDetected
        break
      case GeoCheckStatus.LocationSpoofingDetected:
        errorVisible.value.geoCheck = true
        complianceError.value = ComplianceError.LocationSpoofingDetected
        break
      case GeoCheckStatus.NeighbourState:
        errorVisible.value.neighborState = true
        break
      default:
        errorVisible.value.geoCheck = true
        complianceError.value = ComplianceError.GeoCheckGeneral
        break
    }
  }
  if (restrictions.playerCheck !== PlayerCheckStatus.Ok)
    errorVisible.value.playerRestrictions = true

  if (restrictions.spendingLimit) {
    errorVisible.value.spendingLimit = true
  }
  if (restrictions.depositLimit) {
    errorVisible.value.depositLimit = true
  }
}

onMounted(checkRestrictions)

const handleConfirm = (errorType: keyof (typeof errorVisible)['value']) => {
  errorVisible.value[errorType] = false

  if (closeAllModalsOnConfirm) {
    Object.keys(errorVisible.value).forEach((key) => {
      errorVisible.value[key as keyof typeof errorVisible.value] = false
    })
  }
}

watch(() => restrictions, checkRestrictions)
</script>
